import { useSignal } from '@preact/signals';
import { gridHeight, gridWidth } from '../sim/grid.ts';
import { DEFAULT_BUDGET, TERRAIN_CELL_SIZES, type Resolution } from '../sim/resolution.ts';
import {
  MAX_AREA_SIZE_M,
  MIN_AREA_SIZE_M,
  area,
  cancelLoad,
  gridPlan,
  loadStatus,
  loadTerrain,
  memoryEstimate,
  pendingAreaSize,
  resolution,
  resolutionOverride,
  setAreaSize,
  uiMode,
} from '../state/store.ts';
import { formatNumber } from './format.ts';
import { NumberField } from './NumberField.tsx';

const SNOWPACK_MULTIPLIERS = [1, 2, 3, 4, 5, 10] as const;

/** Panel section shown alongside the map: area size, grid/resolution info, and terrain loading. */
export function AreaPanel() {
  const lockSquare = useSignal(true);
  const size = area.value ?? pendingAreaSize.value;
  const plan = gridPlan.value;
  const mem = memoryEstimate.value;
  const res: Resolution | null = resolution.value;
  const auto = resolutionOverride.value === null;
  const status = loadStatus.value;

  function onWidthChange(w: number): void {
    setAreaSize(w, lockSquare.value ? w : size.height);
  }
  function onHeightChange(h: number): void {
    setAreaSize(lockSquare.value ? h : size.width, h);
  }

  const terrainCell = res?.terrainCell ?? TERRAIN_CELL_SIZES[2];
  const multiplier = res ? Math.max(1, Math.round(res.snowpackCell / res.terrainCell)) : 2;

  function setManual(nextTerrainCell: number, nextMultiplier: number): void {
    resolutionOverride.value = {
      terrainCell: nextTerrainCell,
      snowpackCell: nextTerrainCell * nextMultiplier,
    };
  }

  const overBudget = mem
    ? mem.terrainCells > DEFAULT_BUDGET.terrainCells ||
      mem.snowpackCells > DEFAULT_BUDGET.snowpackCells
    : false;

  return (
    <>
      <section class="panel-section">
        <h2>Area</h2>
        <NumberField
          label="Width"
          value={size.width}
          min={MIN_AREA_SIZE_M}
          max={MAX_AREA_SIZE_M}
          step={50}
          unit="m"
          onChange={onWidthChange}
        />
        <NumberField
          label="Height"
          value={size.height}
          min={MIN_AREA_SIZE_M}
          max={MAX_AREA_SIZE_M}
          step={50}
          unit="m"
          onChange={onHeightChange}
        />
        <label class="checkbox-field">
          <input
            type="checkbox"
            checked={lockSquare.value}
            onChange={(e) => (lockSquare.value = (e.target as HTMLInputElement).checked)}
          />
          Lock square
        </label>
        {!area.value ? <p class="hint">Click the map to place the area.</p> : null}
      </section>

      {uiMode.value === 'advanced' ? (
        <section class="panel-section">
          <h2>Grid</h2>
          {plan && mem ? (
            <>
              <dl class="info-list">
                <dt>Area</dt>
                <dd>
                  {formatNumber((gridWidth(plan.terrain) * gridHeight(plan.terrain)) / 1e6, 2)} km²
                </dd>
                <dt>Terrain cell</dt>
                <dd>{plan.resolution.terrainCell} m</dd>
                <dt>Snowpack cell</dt>
                <dd>{plan.resolution.snowpackCell} m</dd>
                <dt>Terrain cells</dt>
                <dd>{formatNumber(mem.terrainCells)}</dd>
                <dt>Snowpack cells</dt>
                <dd>{formatNumber(mem.snowpackCells)}</dd>
                <dt>Est. memory</dt>
                <dd>{formatNumber(mem.totalBytes / 1e6, 1)} MB</dd>
              </dl>
              {overBudget ? (
                <p class="warning">
                  Above the default cell budget ({formatNumber(DEFAULT_BUDGET.terrainCells)} terrain
                  / {formatNumber(DEFAULT_BUDGET.snowpackCells)} snowpack cells) — loading and
                  simulation will be slower.
                </p>
              ) : null}
            </>
          ) : (
            <p class="hint">Pick an area to see grid info.</p>
          )}
        </section>
      ) : null}

      {uiMode.value === 'advanced' ? (
        <section class="panel-section">
          <h2>Resolution</h2>
          <label class="radio-field">
            <input
              type="radio"
              name="res-mode"
              checked={auto}
              onChange={() => (resolutionOverride.value = null)}
            />
            Auto
          </label>
          <label class="radio-field">
            <input
              type="radio"
              name="res-mode"
              checked={!auto}
              onChange={() => setManual(terrainCell, multiplier)}
            />
            Manual
          </label>
          <div class="field-row">
            <span class="field-label">Terrain cell</span>
            <select
              disabled={auto}
              value={terrainCell}
              onChange={(e) => setManual(Number((e.target as HTMLSelectElement).value), multiplier)}
            >
              {TERRAIN_CELL_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s} m
                </option>
              ))}
            </select>
          </div>
          <div class="field-row">
            <span class="field-label">Snowpack multiplier</span>
            <select
              disabled={auto}
              value={multiplier}
              onChange={(e) =>
                setManual(terrainCell, Number((e.target as HTMLSelectElement).value))
              }
            >
              {SNOWPACK_MULTIPLIERS.map((m) => (
                <option key={m} value={m}>
                  ×{m} ({terrainCell * m} m)
                </option>
              ))}
            </select>
          </div>
        </section>
      ) : null}

      <section class="panel-section">
        <button
          class="primary"
          onClick={() => void loadTerrain()}
          disabled={!area.value || status.kind === 'loading'}
        >
          Load terrain
        </button>
        {status.kind === 'loading' ? (
          <div class="load-progress">
            <p>
              Loading tiles: {status.done}/{status.total}
            </p>
            <progress value={status.done} max={Math.max(1, status.total)} />
            <button onClick={cancelLoad}>Cancel</button>
          </div>
        ) : null}
        {status.kind === 'error' ? <p class="error">{status.message}</p> : null}
      </section>
    </>
  );
}
